/**
 * securityPosture.js — ONE answer to "what is this account's 2FA state".
 *
 * ★★★ WHY. Two screens described the same account two different ways on the same
 *     build, six minutes apart (TestFlight, Build 47, 2026-08-24):
 *
 *       Settings › Security   "Two-factor auth — Enabled · TOTP + SMS"
 *       Security              "Two-factor authentication is not set up yet."
 *
 *     They were not two readers disagreeing. The Settings row read NOTHING —
 *     `subtitle={t('settingsMisc.enabledTotpSms')}` is a literal, rendered
 *     unconditionally for every account whatever its real state. The row directly
 *     above it, Session PIN, reads live state and even refreshes it on mount. One
 *     row was built and the one beside it was decorated.
 *
 * ★★★ AND THE HONEST READER HAD ITS OWN HOLE. SecurityScreen caught a failed
 *     status fetch, logged it, and left `status` null — which rendered IDENTICALLY
 *     to "not set up". A patient with 2FA on, on a flaky connection, was told they
 *     had none and offered a button to set it up. That is the house defect (a
 *     silent catch on a config fetch) landing on a security surface, where the
 *     failure mode is not a wrong number but a wrong statement about protection.
 *
 * ★★★ SO `error` AND `off` ARE DIFFERENT STATES HERE, DELIBERATELY, and a null
 *     status after loading has finished is an ERROR — never an "off". Nothing may
 *     tell a patient their account is unprotected because a fetch failed.
 *
 * Pure. No fetching, no clock, no i18n — it returns a state, callers render it.
 */

export const POSTURE_LOADING = 'loading'
export const POSTURE_ERROR = 'error'
export const POSTURE_SSO = 'sso'
export const POSTURE_ON = 'on'
export const POSTURE_OFF = 'off'

/**
 * @param {{loading?: boolean, error?: any, status?: object|null}} input
 * @returns {'loading'|'error'|'sso'|'on'|'off'}
 */
export function twoFactorPosture({ loading, error, status } = {}) {
  if (loading) return POSTURE_LOADING
  if (error) return POSTURE_ERROR
  // ★ A null status here means the fetch resolved to nothing — i.e. it failed.
  //   Reading that as "off" is what put "Not set up" over a protected account.
  if (!status || typeof status !== 'object') return POSTURE_ERROR
  if (status.isSsoOnly) return POSTURE_SSO
  return status.enabled ? POSTURE_ON : POSTURE_OFF
}

/** True only when we positively know the account is unprotected. */
export function isKnownUnprotected(input) {
  return twoFactorPosture(input) === POSTURE_OFF
}

/** The provider behind an SSO-only account, or null. */
export function ssoProvider(status) {
  const p = (status && status.authProvider) || ''
  if (p.includes('google')) return 'google'
  if (p.includes('apple')) return 'apple'
  return null
}

/**
 * The method actually on file. Returns a stable id, never prose — the caller
 * translates. `null` when nothing is active, so a caller cannot print a method
 * name over an account that has none (which is how "TOTP + SMS" was shown to
 * accounts with neither).
 */
export function activeMethod(status) {
  if (!status || !status.enabled) return null
  return status.method === 'sms' ? 'sms' : 'totp'
}
